import type React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Calendar,
  CalendarCheck,
  ArrowLeftRight,
  BarChart3,
  Settings,
  X,
  Heart,
  ExternalLink,
  UserCog,
  BookOpen,
  Inbox,
  Gauge,
  Megaphone,
  DoorOpen,
  ScrollText,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useCongregation } from '../../contexts/CongregationContext';
import { useSidebar } from './Layout';
import { useTutorial } from '../../contexts/TutorialContext';
import { useUIPrefs } from '../../contexts/UIPrefsContext';
import Logo from '../Logo';

import type { HideableSection } from '../../contexts/UIPrefsContext';

const navigation: { name: string; href: string; icon: React.ElementType; id: string; hideKey?: HideableSection }[] = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard, id: 'nav-dashboard' },
  { name: 'Mijn Rooster', href: '/mijn-rooster', icon: CalendarCheck, id: 'nav-mijn-rooster' },
  { name: 'Beschikbaarheid', href: '/beschikbaarheid', icon: Calendar, id: 'nav-beschikbaarheid' },
  { name: 'Ruilverzoeken', href: '/ruilverzoeken', icon: ArrowLeftRight, id: 'nav-ruilverzoeken' },
  { name: 'Planning', href: '/planning', icon: Calendar, id: 'nav-planning' },
  { name: 'Leerhuis', href: '/leerhuis', icon: BookOpen, id: 'nav-leerhuis', hideKey: 'leerhuis' },
  { name: 'Vrije diensten', href: '/vrije-diensten', icon: Inbox, id: 'nav-vrije-diensten', hideKey: 'vrije-diensten' },
  { name: 'Berichten', href: '/berichten', icon: Megaphone, id: 'nav-berichten' },
  { name: 'Instellingen', href: '/instellingen', icon: Settings, id: 'nav-instellingen' },
];

const adminNavigation: { name: string; href: string; icon: React.ElementType; id: string; hideKey?: HideableSection }[] = [
  { name: 'Coordinator', href: '/coordinator', icon: Gauge, id: 'nav-coordinator' },
  { name: 'Teams', href: '/teams', icon: Users, id: 'nav-teams' },
  { name: 'Zalen', href: '/zalen', icon: DoorOpen, id: 'nav-zalen' },
  { name: 'Rapportages', href: '/rapportages', icon: BarChart3, id: 'nav-rapportages', hideKey: 'rapportages' },
];

const superAdminNavigation = [
  { name: 'Gebruikers', href: '/gebruikers', icon: UserCog, id: 'nav-gebruikers' },
  { name: 'Auditlog', href: '/auditlog', icon: ScrollText, id: 'nav-auditlog' },
];

const NAV_BG = '#0D1B2A';
const NAV_HEADER_BG = '#13243A';
const NAV_BORDER = '#1A2F4A';
const NAV_TEXT_MUTED = '#A3E4D7';
const NAV_TEXT_SECTION = '#84D2C5';
const NAV_TEXT_LIGHT = '#FFFFFF';
const NAV_ACTIVE_BG = 'var(--color-primary)';
const NAV_HOVER_BG = '#1A2F4A';

export default function Sidebar() {
  const { profile } = useAuth();
  const { active } = useCongregation();
  const { isOpen, close } = useSidebar();
  const { showBadge, dismissBadge } = useTutorial();
  const { isSectionHidden } = useUIPrefs();
  const isAdmin = profile?.role === 'superadmin' || profile?.role === 'beheerder' || profile?.role === 'groepsbeheerder';
  const isSuperAdmin = profile?.role === 'superadmin' || profile?.role === 'beheerder';

  const visibleNavigation = navigation.filter(item => !item.hideKey || !isSectionHidden(item.hideKey));
  const visibleAdminNavigation = adminNavigation.filter(item => !item.hideKey || !isSectionHidden(item.hideKey));

  return (
    <div
      className={`
        fixed inset-y-0 left-0 z-50 w-64 flex flex-col transform transition-transform duration-300 ease-in-out
        lg:relative lg:translate-x-0
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}
      style={{ backgroundColor: NAV_BG }}
    >
      <div
        className="flex items-center justify-between h-16 px-4 flex-shrink-0 border-b"
        style={{ backgroundColor: NAV_HEADER_BG, borderColor: NAV_BORDER }}
      >
        <div className="flex items-center gap-3">
          <Logo size="sm" />
          <div className="flex flex-col">
            <span
              className="text-base font-semibold leading-tight"
              style={{ fontFamily: 'Inter, system-ui, sans-serif', color: NAV_TEXT_LIGHT }}
            >
              kerkplanner
            </span>
            {active && !active.is_primary && (
              <span className="text-[10px] font-medium leading-tight" style={{ color: NAV_TEXT_MUTED }}>
                {active.short_name}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={close}
          className="lg:hidden p-2 rounded-lg transition-colors"
          style={{ color: NAV_TEXT_MUTED }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = NAV_HOVER_BG; (e.currentTarget as HTMLButtonElement).style.color = NAV_TEXT_LIGHT; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = NAV_TEXT_MUTED; }}
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {visibleNavigation.map((item) => (
          <NavLink
            key={item.name}
            id={item.id}
            to={item.href}
            onClick={() => {
              close();
              if (item.id) dismissBadge(item.id);
            }}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 relative ${
                isActive ? '' : ''
              }`
            }
            style={({ isActive }) =>
              isActive
                ? { backgroundColor: NAV_ACTIVE_BG, color: '#ffffff' }
                : { color: NAV_TEXT_MUTED }
            }
            onMouseEnter={e => {
              const el = e.currentTarget as HTMLAnchorElement;
              if (!el.getAttribute('aria-current')) {
                el.style.backgroundColor = NAV_HOVER_BG;
                el.style.color = NAV_TEXT_LIGHT;
              }
            }}
            onMouseLeave={e => {
              const el = e.currentTarget as HTMLAnchorElement;
              if (!el.getAttribute('aria-current')) {
                el.style.backgroundColor = '';
                el.style.color = NAV_TEXT_MUTED;
              }
            }}
          >
            {() => (
              <>
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {item.name}
                {item.id && showBadge(item.id) && (
                  <span className="absolute right-3 w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: NAV_TEXT_MUTED }} />
                )}
              </>
            )}
          </NavLink>
        ))}

        {isAdmin && (
          <>
            <div className="pt-5 pb-2 px-3">
              <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: NAV_TEXT_SECTION }}>
                Beheer
              </p>
            </div>
            {visibleAdminNavigation.map((item) => (
              <NavLink
                key={item.name}
                id={item.id}
                to={item.href}
                onClick={close}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150"
                style={({ isActive }) =>
                  isActive
                    ? { backgroundColor: NAV_ACTIVE_BG, color: '#ffffff' }
                    : { color: NAV_TEXT_MUTED }
                }
                onMouseEnter={e => {
                  const el = e.currentTarget as HTMLAnchorElement;
                  if (!el.getAttribute('aria-current')) {
                    el.style.backgroundColor = NAV_HOVER_BG;
                    el.style.color = NAV_TEXT_LIGHT;
                  }
                }}
                onMouseLeave={e => {
                  const el = e.currentTarget as HTMLAnchorElement;
                  if (!el.getAttribute('aria-current')) {
                    el.style.backgroundColor = '';
                    el.style.color = NAV_TEXT_MUTED;
                  }
                }}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {item.name}
              </NavLink>
            ))}
          </>
        )}

        {isSuperAdmin && (
          <>
            <div className="pt-5 pb-2 px-3">
              <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: NAV_TEXT_SECTION }}>
                Systeem
              </p>
            </div>
            {superAdminNavigation.map((item) => (
              <NavLink
                key={item.name}
                id={item.id}
                to={item.href}
                onClick={close}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150"
                style={({ isActive }) =>
                  isActive
                    ? { backgroundColor: NAV_ACTIVE_BG, color: '#ffffff' }
                    : { color: NAV_TEXT_MUTED }
                }
                onMouseEnter={e => {
                  const el = e.currentTarget as HTMLAnchorElement;
                  if (!el.getAttribute('aria-current')) {
                    el.style.backgroundColor = NAV_HOVER_BG;
                    el.style.color = NAV_TEXT_LIGHT;
                  }
                }}
                onMouseLeave={e => {
                  const el = e.currentTarget as HTMLAnchorElement;
                  if (!el.getAttribute('aria-current')) {
                    el.style.backgroundColor = '';
                    el.style.color = NAV_TEXT_MUTED;
                  }
                }}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {item.name}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      <div className="p-4 flex-shrink-0 border-t" style={{ borderColor: NAV_BORDER }}>
        <div className="text-center space-y-1.5">
          <p className="text-xs flex items-center justify-center gap-1" style={{ color: NAV_TEXT_SECTION }}>
            Made with <Heart className="w-3 h-3 fill-current" style={{ color: NAV_TEXT_MUTED }} /> by{' '}
            <a
              href="https://joy-ict.nl"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors hover:underline"
              style={{ color: NAV_TEXT_MUTED }}
            >
              Joy ICT
            </a>
          </p>
          <a
            href="https://joy-ict.nl/kerkplanner"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs transition-colors flex items-center justify-center gap-1"
            style={{ color: NAV_TEXT_SECTION }}
          >
            Over deze app <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
}
